import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { KATEGORIEN } from "./enums";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "left",
    padding: "0",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflow: "hidden",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, transform, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(45,20,43,0.08)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: `0 0 0 4px ${BRAND.purple}22`,
  },
  illust: {
    backgroundColor: "#f5eef6",
    paddingTop: "20px",
    paddingBottom: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  body: {
    paddingTop: "12px",
    paddingBottom: "14px",
    paddingLeft: "14px",
    paddingRight: "14px",
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    color: BRAND.muted,
    marginTop: "4px",
  },
});

const Wohnen = () => (
  <svg width="84" height="60" viewBox="0 0 84 60" fill="none">
    <path d="M6 50V30L42 8l36 22v20H56V36H28v14H6z" fill={BRAND.purple} />
    <rect x="14" y="36" width="8" height="10" fill={BRAND.gold} />
    <rect x="60" y="36" width="8" height="10" fill={BRAND.gold} />
    <rect x="36" y="42" width="12" height="8" fill={BRAND.gold} />
  </svg>
);

const Mischnutzung = () => (
  <svg width="84" height="60" viewBox="0 0 84 60" fill="none">
    <path d="M6 50V30L24 16l18 14v20H6z" fill={BRAND.purple} />
    <rect x="42" y="22" width="36" height="28" fill={BRAND.gold} />
    <rect x="14" y="34" width="6" height="8" fill={BRAND.gold} />
    <rect x="48" y="32" width="8" height="10" fill={BRAND.bg} />
    <rect x="64" y="32" width="8" height="10" fill={BRAND.bg} />
  </svg>
);

const Gewerbe = () => (
  <svg width="84" height="60" viewBox="0 0 84 60" fill="none">
    <path d="M6 50V20l16-6v8l16-6v8l16-6v32H6z" fill={BRAND.purple} />
    <rect x="58" y="28" width="20" height="22" fill={BRAND.gold} />
    <rect x="14" y="32" width="6" height="8" fill={BRAND.bg} />
    <rect x="26" y="32" width="6" height="8" fill={BRAND.bg} />
    <rect x="38" y="32" width="6" height="8" fill={BRAND.bg} />
    <rect x="62" y="36" width="12" height="14" fill={BRAND.bg} />
  </svg>
);

const HINTS: Record<string, string> = {
  "Wohnen (100%)": "Wohnimmobilie ohne gewerbliche Nutzung",
  "Mischnutzung (Wohnen u. Gewerbe)": "Teils Wohnung, teils Gewerbe",
  "Gewerbe (100%)": "Reine gewerbliche Nutzung",
};

const ICONS: Record<string, () => JSX.Element> = {
  "Wohnen (100%)": Wohnen,
  "Mischnutzung (Wohnen u. Gewerbe)": Mischnutzung,
  "Gewerbe (100%)": Gewerbe,
};

type Props = {
  value: string;
  onChange: (next: string) => void;
};

const KategoriePicker = ({ value, onChange }: Props) => {
  const styles = useStyles();
  return (
    <div className={styles.grid}>
      {KATEGORIEN.map((k) => {
        const Icon = ICONS[k];
        const active = value === k;
        return (
          <button
            type="button"
            key={k}
            className={mergeClasses(styles.tile, active && styles.tileActive)}
            onClick={() => onChange(k)}
          >
            <div className={styles.illust}>
              <Icon />
            </div>
            <div className={styles.body}>
              <div className={styles.title}>{k}</div>
              <div className={styles.hint}>{HINTS[k]}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default KategoriePicker;
